import React, { useEffect, useState } from 'react';
import { renewals, clients } from './api';

// =============================================================================
// LinkToClientModal — search-and-pick a client folder for a renewal.
// v5.6.0 (step 12b): moved out of Renewals.jsx (the workspace uses it too). The
// search UI is unchanged (design B: "keep the existing LinkToClientModal UI"); the
// write path is new:
//   - unlinked chain           -> PUT /renewals/policies/{id}/client {client_id}
//   - linked to another client -> GET /renewals/policies/{id}/relink-preview, shown
//     plainly (moves / stays + why / blocking); Confirm (disabled while blocking)
//     -> PUT with expected_file_ids + expected_current_client_id; a 409 "review
//     again" re-runs the preview (addendum f, delta 12)
// The old PATCH /renewals/policies/{id} {client_id} link path is retired.
// Errors (cabinet, restricted, deleted client, …) show the server's message.
// onSaved(responseData) — the caller uses responseData.trees_changed.
// =============================================================================

const detailOf = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return e?.message || 'Something went wrong';
};

export default function LinkToClientModal({ policyId, currentName, currentClientId, currentClientName, onClose, onSaved, darkMode }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const subPanelBg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const inputBg = darkMode ? '#1a1a1a' : '#fff';

  const [query, setQuery] = useState(currentName || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);   // relink-preview payload while confirming a re-link
  const [notice, setNotice] = useState(null);     // "things changed — review again"

  // Debounced search (unchanged)
  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(() => {
      setSearching(true);
      clients.search(query)
        .then((r) => setResults((r.data?.results) || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const finish = (data) => { if (onSaved) onSaved(data); onClose(); };

  const loadPreview = async (clientId) => {
    const r = await renewals.relinkPreview(policyId, clientId);
    setPreview(r.data);
    return r.data;
  };

  const pick = async (clientId) => {
    setSaving(true); setError(null); setNotice(null);
    try {
      if (currentClientId && clientId !== currentClientId) {
        await loadPreview(clientId);                       // re-link: show what will happen first
        setSaving(false);
        return;
      }
      const r = await renewals.linkPolicyClient(policyId, { client_id: clientId });
      finish(r.data);
    } catch (e) {
      setError(detailOf(e));
      setSaving(false);
    }
  };

  const confirm = async () => {
    if (!preview || preview.blocking?.length) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const r = await renewals.linkPolicyClient(policyId, {
        client_id: preview.to_client_id,
        expected_file_ids: preview.expected_file_ids,
        expected_current_client_id: preview.expected_current_client_id,
      });
      finish(r.data);
    } catch (e) {
      const msg = detailOf(e);
      if (e?.response?.status === 409 && /review again/i.test(msg)) {
        try {
          await loadPreview(preview.to_client_id);
          setNotice('Something changed since this preview — please review it again.');
        } catch (e2) { setError(detailOf(e2)); }
      } else {
        setError(msg);
      }
      setSaving(false);
    }
  };

  const btn = (primary, disabled) => ({
    padding: '6px 14px', fontSize: 12, borderRadius: 5, cursor: disabled ? 'default' : 'pointer',
    background: primary ? '#185FA5' : panelBg, color: primary ? '#fff' : textPrimary,
    border: primary ? 'none' : `1px solid ${borderColor}`, opacity: disabled ? 0.5 : 1,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div role="dialog" aria-label="Link to client folder" style={{
        background: panelBg, color: textPrimary, borderRadius: 8,
        width: 560, maxWidth: '90vw', maxHeight: '80vh',
        display: 'flex', flexDirection: 'column',
        border: `1px solid ${borderColor}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#185FA5' }}>
            {currentClientId ? '🔗 Change client folder' : '🔗 Link to Client Folder'}
          </span>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} disabled={saving} style={btn(false, saving)}>Cancel</button>
        </div>

        {preview ? (
          <div data-testid="relink-preview" style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>
              Move this renewal from {preview.from_client_name || currentClientName || 'its current client'} to {preview.to_client_name}?
            </div>
            <div data-testid="relink-message">{preview.message}</div>
            {notice && <div role="status" style={{ color: '#B8860B' }}>{notice}</div>}
            {preview.moves?.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, color: textSecondary }}>Will move ({preview.moves.length})</div>
                <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                  {preview.moves.map((m) => (
                    <li key={m.file_id}>{m.filename} → {m.to_folder}{m.trashed ? ' (in Trash)' : ''}{m.versioned ? ' (has editor versions)' : ''}</li>
                  ))}
                </ul>
              </div>
            )}
            {preview.stays?.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, color: textSecondary }}>Stays where it is ({preview.stays.length})</div>
                <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                  {preview.stays.map((s) => <li key={s.file_id}>{s.filename} — {s.reason}</li>)}
                </ul>
              </div>
            )}
            {preview.blocking?.length > 0 && (
              <div data-testid="relink-blocking" role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: 12 }}>
                Can't re-link yet: {preview.blocking.join(' · ')}
              </div>
            )}
            {error && <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: 12 }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button onClick={() => { setPreview(null); setNotice(null); setError(null); }} disabled={saving} style={btn(false, saving)}>Back</button>
              <button onClick={confirm} disabled={saving || preview.blocking?.length > 0}
                style={btn(true, saving || preview.blocking?.length > 0)}>{saving ? 'Moving…' : 'Confirm re-link'}</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 12, color: textSecondary, marginBottom: 8 }}>
                {currentClientId
                  ? `Currently linked to ${currentClientName || 'a client'}. Search for the client folder to move this renewal to:`
                  : 'Search for the client folder to link this renewal to:'}
              </div>
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a client name…"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14,
                  background: inputBg, color: textPrimary,
                  border: `1px solid ${borderColor}`, borderRadius: 5,
                  boxSizing: 'border-box',
                }}
              />
              {error && (
                <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: 12, marginTop: 10 }}>
                  {error}
                </div>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px 18px' }}>
              {searching && (
                <div style={{ padding: 12, textAlign: 'center', color: textSecondary, fontSize: 12 }}>Searching…</div>
              )}
              {!searching && query.length >= 2 && results.length === 0 && (
                <div style={{ padding: 12, textAlign: 'center', color: textSecondary, fontSize: 12 }}>No matches found.</div>
              )}
              {results.map(r => (
                <div
                  key={r.id}
                  data-testid={`client-result-${r.id}`}
                  onClick={() => !saving && pick(r.id)}
                  style={{
                    padding: '10px 12px', borderRadius: 5, cursor: saving ? 'wait' : 'pointer',
                    background: subPanelBg, marginBottom: 6,
                    border: `1px solid ${borderColor}`, fontSize: 13,
                    opacity: saving ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = '#185FA5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = subPanelBg; }}
                >
                  <div style={{ fontWeight: 600 }}>{r.name}{r.id === currentClientId ? ' (current)' : ''}</div>
                  {r.address_name && (
                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>{r.address_name}</div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
